import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { LeaderboardCard } from "./LeaderboardCard";
import { MyRankCard } from "./MyRankCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Trophy } from "lucide-react";

interface LeaderboardEntry {
  user_id: string;
  username: string;
  name: string | null;
  image: string | null;
  image_thumb: string | null;
  total_xp: number;
  level: number;
  level_title: string | null;
  books_read_count: number;
  total_achievements_unlocked: number;
  current_streak_days: number;
  rank: number;
  books_read_this_week?: number;
  books_read_this_month?: number;
}

interface LeaderboardListProps {
  type: "xp" | "books" | "streak" | "achievements";
  timeframe: "all-time" | "month" | "week";
}

export function LeaderboardList({ type, timeframe }: LeaderboardListProps) {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["leaderboard", type, timeframe],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("get-leaderboard", {
        body: { type, timeframe, limit: 100 },
      });

      if (error) throw error;
      return data as {
        leaderboard: LeaderboardEntry[];
        currentUser: LeaderboardEntry | null;
        total: number;
      };
    },
    refetchInterval: 60000, // Refresh every minute
  });

  const getStatLabel = () => {
    switch (type) {
      case "xp":
        return "XP";
      case "books":
        return timeframe === "week" ? "Books This Week" : timeframe === "month" ? "Books This Month" : "Books Read";
      case "streak":
        return "Day Streak";
      case "achievements":
        return "Achievements";
    }
  };

  const getStatValue = (entry: LeaderboardEntry) => {
    switch (type) {
      case "xp":
        return entry.total_xp.toLocaleString();
      case "books":
        if (timeframe === "week") return entry.books_read_this_week?.toString() || "0";
        if (timeframe === "month") return entry.books_read_this_month?.toString() || "0";
        return entry.books_read_count.toString();
      case "streak":
        return entry.current_streak_days.toString();
      case "achievements":
        return entry.total_achievements_unlocked.toString();
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    );
  }

  if (!data?.leaderboard || data.leaderboard.length === 0) {
    return (
      <div className="text-center py-12">
        <Trophy className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
        <h3 className="text-lg font-semibold mb-2">No Rankings Yet</h3>
        <p className="text-muted-foreground">
          Be the first to climb the leaderboard!
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Current user rank card if not in top results */}
      {data.currentUser && !data.leaderboard.find(e => e.user_id === user?.id) && (
        <MyRankCard
          entry={data.currentUser}
          statLabel={getStatLabel()}
          statValue={getStatValue(data.currentUser)}
        />
      )}

      {/* Top 3 podium */}
      {data.leaderboard.length >= 3 && (
        <div className="grid grid-cols-3 gap-4 mb-8">
          {/* 2nd place */}
          <div className="flex flex-col items-center pt-8">
            <div className="text-4xl mb-2">🥈</div>
            <LeaderboardCard
              entry={data.leaderboard[1]}
              statLabel={getStatLabel()}
              statValue={getStatValue(data.leaderboard[1])}
              isCurrentUser={data.leaderboard[1].user_id === user?.id}
              compact
            />
          </div>
          
          {/* 1st place */}
          <div className="flex flex-col items-center">
            <div className="text-5xl mb-2">🏆</div>
            <LeaderboardCard
              entry={data.leaderboard[0]}
              statLabel={getStatLabel()}
              statValue={getStatValue(data.leaderboard[0])}
              isCurrentUser={data.leaderboard[0].user_id === user?.id}
              compact
            />
          </div>
          
          {/* 3rd place */}
          <div className="flex flex-col items-center pt-12">
            <div className="text-3xl mb-2">🥉</div>
            <LeaderboardCard
              entry={data.leaderboard[2]}
              statLabel={getStatLabel()}
              statValue={getStatValue(data.leaderboard[2])}
              isCurrentUser={data.leaderboard[2].user_id === user?.id}
              compact
            />
          </div>
        </div>
      )}

      {/* Rest of the leaderboard */}
      <div className="space-y-2">
        {data.leaderboard.slice(3).map((entry) => (
          <LeaderboardCard
            key={entry.user_id}
            entry={entry}
            statLabel={getStatLabel()}
            statValue={getStatValue(entry)}
            isCurrentUser={entry.user_id === user?.id}
          />
        ))}
      </div>
    </div>
  );
}
